import { createAction, createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { axiosPrivate} from '../hooks/axiosInstance';

// Fetch all dev projects
export const getDevProjects = createAsyncThunk(
  'devproject/getDevProjects',
  async () => {
    const { data } = await axiosPrivate.get(`/admin/devprojects`);
    return data;
  }
);

// Create a new dev project (without body)
export const createDevProject = createAsyncThunk(
  'devproject/addDevProject',
  async ({ formData }) => {
    try {
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      };
      const response = await axiosPrivate.post(`/admin/devproject`, formData, config);
      
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }
);

// Update dev project (excluding body content)
export const updateDevProject = createAsyncThunk(
  'devproject/updateDevProject',
  async ({ id, formData }) => {
    console.log('Updating dev project with ID:', id);
    try {
      // Check if formData contains a file
      const hasFile = formData.get('file') !== null;
      
      const config = {
        headers: {
          'Content-Type': hasFile ? 'multipart/form-data' : 'application/json'
        }
      };

      // If no file, convert FormData to regular object for JSON
      let payload = formData;
      if (!hasFile) {
        payload = {};
        for (let [key, value] of formData.entries()) {
          // Exclude body from regular update
          if (key !== 'body') {
            payload[key] = value;
          }
        }
      }

      const response = await axiosPrivate.put(`/admin/devproject/${id}`, payload, config);
      
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }
);

// Update only body content for dev project
export const updateDevProjectBody = createAsyncThunk(
  'devproject/updateDevProjectBody',
  async ({ id, body }) => {
    console.log('Updating dev project body with ID:', id);
    try {
      const response = await axiosPrivate.put(`/admin/devproject/${id}/body`, { body }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }
);

// Get only body content for dev project
export const getDevProjectBody = createAsyncThunk(
  'devproject/getDevProjectBody',
  async (id) => {
    console.log('Getting dev project body with ID:', id);
    try {
      const response = await axiosPrivate.get(`/admin/devproject/${id}/body`);
      
      return { id, body: response.data.body };
    } catch (error) {
      console.log(error);
      throw error;
    }
  }
);

// Delete dev project by ID
export const deleteDevProjectById = createAsyncThunk(
  'devproject/deleteDevProjectById',
  async (projectId, thunkAPI) => {
    console.log('Deleting dev project with ID:', projectId);
    try {
      await axiosPrivate.delete(`/admin/devproject/${projectId}`);
      return projectId;
    } catch (error) {
      console.log(error);
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

// Delete all dev projects
export const deleteAllDevProjects = createAsyncThunk(
  'devproject/deleteAllDevProjects',
  async () => {
    await axiosPrivate.delete(`/admin/devprojects/`);
  }
);

export const clearField = createAction('devproject/clearField');

export const devprojectSlice = createSlice({
  name: 'devprojects',
  initialState: {
    devprojects: [],
    devproject: null,
    loading: false,
    error: null,
    updating: false,
    bodyLoading: false,
    bodyError: null,
  },
  reducers: {
    clearField: (state) => {
      state.devproject = null;
      state.error = null;
      state.bodyError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get dev projects
      .addCase(getDevProjects.pending, (state) => {
        state.loading = true;
      })
      .addCase(getDevProjects.fulfilled, (state, action) => {
        state.devprojects = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(getDevProjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Create dev project
      .addCase(createDevProject.pending, (state) => {
        state.loading = true;
      })
      .addCase(createDevProject.fulfilled, (state, action) => {
        state.loading = false;
        state.devproject = action.payload;
        // Add new project to the list
        state.devprojects.unshift(action.payload);
        state.error = null;
        console.log('Dev project created:', action.payload);
      })
      .addCase(createDevProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Update dev project
      .addCase(updateDevProject.pending, (state) => {
        state.updating = true;
      })
      .addCase(updateDevProject.fulfilled, (state, action) => {
        state.updating = false;
        const updatedProject = action.payload;
        const index = state.devprojects.findIndex(
          (project) => project._id === updatedProject._id
        );
        if (index !== -1) {
          state.devprojects[index] = updatedProject;
        }
        state.error = null;
        console.log('Dev project updated:', updatedProject);
      })
      .addCase(updateDevProject.rejected, (state, action) => {
        state.updating = false;
        state.error = action.error.message;
      })

      // Update dev project body
      .addCase(updateDevProjectBody.pending, (state) => {
        state.bodyLoading = true;
        state.bodyError = null;
      })
      .addCase(updateDevProjectBody.fulfilled, (state, action) => {
        state.bodyLoading = false;
        const updatedProject = action.payload;
        const index = state.devprojects.findIndex(
          (project) => project._id === updatedProject._id
        );
        if (index !== -1) {
          state.devprojects[index] = updatedProject;
        }
        state.bodyError = null;
        console.log('Dev project body updated:', updatedProject);
      })
      .addCase(updateDevProjectBody.rejected, (state, action) => {
        state.bodyLoading = false;
        state.bodyError = action.error.message;
      })

      // Get dev project body
      .addCase(getDevProjectBody.pending, (state) => {
        state.bodyLoading = true;
        state.bodyError = null;
      })
      .addCase(getDevProjectBody.fulfilled, (state, action) => {
        state.bodyLoading = false;
        const { id, body } = action.payload;
        const index = state.devprojects.findIndex(
          (project) => project._id === id
        );
        if (index !== -1) {
          state.devprojects[index].body = body;
        }
        state.bodyError = null;
        console.log('Dev project body fetched:', action.payload);
      })
      .addCase(getDevProjectBody.rejected, (state, action) => {
        state.bodyLoading = false;
        state.bodyError = action.error.message;
      })

      // Delete dev project by ID
      .addCase(deleteDevProjectById.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteDevProjectById.fulfilled, (state, action) => {
        state.loading = false;
        const deletedProjectId = action.payload;
        state.devprojects = state.devprojects.filter(
          (project) => project._id !== deletedProjectId
        );
        state.error = null;
      })
      .addCase(deleteDevProjectById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Delete all dev projects
      .addCase(deleteAllDevProjects.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteAllDevProjects.fulfilled, (state) => {
        state.devprojects = [];
        state.loading = false;
        state.error = null;
      })
      .addCase(deleteAllDevProjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export default devprojectSlice.reducer;